import { useContext, type ReactNode } from "react";
import { TemplatesContext, type TemplateMap } from "./TemplatesContext";
import { isSlotClaimedElsewhere, type SlotClaimMap } from "./slotClaimMap";

export const useTemplates = (): TemplateMap => useContext(TemplatesContext).templates;

export const useTemplate = (id: string): ReactNode | undefined =>
  useContext(TemplatesContext).templates.get(id);

/**
 * Access the global slot-claim map (which slot ids are claimed in which
 * embedder templates). Most consumers should use the higher-level
 * `useSlotClaimedElsewhere` instead.
 */
export const useSlotClaims = (): SlotClaimMap => useContext(TemplatesContext).claims;

/**
 * Returns `true` when this slot id is claimed by an embedder template other
 * than `activeTemplateId`. Used by `<Slot>` to suppress default rendering
 * when content has been relocated to a cross-template position.
 *
 * Pass `null` for `activeTemplateId` when the consumer isn't inside any
 * `<Templated>` shell — any claim from any template then counts as
 * "elsewhere" and the slot is suppressed.
 */
export const useSlotClaimedElsewhere = (
  slotId: string,
  activeTemplateId: string | null,
): boolean => {
  const claims = useContext(TemplatesContext).claims;
  return isSlotClaimedElsewhere(claims, slotId, activeTemplateId);
};
